import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StepShell from "../components/StepShell";
import AyupathCard from "../components/AyupathCard";
import Field from "../components/Field";
import Select from "../components/Select";
import PrimaryButton from "../components/PrimaryButton";
import { useForm } from "../context/FormContext";
import { track } from "../utils/analytics";
import useEnterToContinue from "../hooks/useEnterToContinue";

const TOTAL = 6;

const CONFIDENCE_OPTIONS = [
  ["exact", "To the minute", "From my birth certificate, hospital record or app"],
  ["approximate", "Roughly right", "Within about 15 minutes — family memory, not a record"],
  ["unknown", "I'm honestly not sure", "A guess is the best I can do"],
];

// Birth time drives the house cusps, so an unflagged guess reads as a confident
// wrong answer. We ask how sure they are and tell the backend to hold back.
const CONFIDENCE_CONSEQUENCE = {
  approximate: "We'll lean on the parts of your chart that hold steady, and leave out the timing-sensitive details rather than guess at them.",
  unknown: "You'll still get a real reading — we'll use what doesn't depend on exact timing, and skip the rest instead of making it up.",
};

export default function StepBirthTime() {
  const { formData, setFormData } = useForm();
  const navigate = useNavigate();

  const hours = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);

  const { hour, minute, meridiem } = formData.birthTime;
  const confidence = formData.birthTimeConfidence;
  const canContinue = hour !== "" && minute !== "" && meridiem !== "" && confidence !== "";

  const update = (patch) => {
    setFormData({ ...formData, birthTime: { ...formData.birthTime, ...patch }});
  };

  function next() {
    track("birth_time_confidence_selected", { confidence });
    navigate("/location");
  }

  useEnterToContinue(next, canContinue);

  return (
    <StepShell step={3} totalSteps={TOTAL} title="Your next chapter">
      <AyupathCard>
        <div className="space-y-5">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">What time were you born?</h2>
            <p className="text-gray-600 mt-1">
              We're not asking you for payment — we're asking for the one detail that
              decides whether this reading is actually about you.
            </p>
          </div>

          <div className="rounded-2xl bg-orange-50 border border-orange-100 p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-gray-900">Minutes matter here.</span>{" "}
              Your birth time sets the structure of your chart. Five minutes out and most
              of it shifts. Your birth certificate is the best source — it's worth the
              look before you continue.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <span className="font-semibold text-gray-900">Have a family horoscope?</span>{" "}
              If one was written when you were born (a kendara, jathakam or kundli), it
              records your birth time to the minute. A parent may also have it on a
              hospital card.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Hour">
              <Select value={hour} onChange={(e) => update({ hour: Number(e.target.value) })}>
                <option value="">--</option>
                {hours.map((h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                ))}
              </Select>
            </Field>

            <Field label="Minute">
              <Select value={minute} onChange={(e) => update({ minute: Number(e.target.value) })}>
                <option value="">--</option>
                {minutes.map((m) => (
                  <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                ))}
              </Select>
            </Field>

            <Field label="AM/PM">
              <Select value={meridiem} onChange={(e) => update({ meridiem: e.target.value })}>
                <option value="">--</option>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </Select>
            </Field>
          </div>

          <fieldset className="space-y-3">
            <legend className="font-semibold text-gray-900">How sure are you about that time?</legend>
            <p className="text-sm text-gray-600">
              There's no wrong answer. This tells us how much weight your chart can carry.
            </p>
            {CONFIDENCE_OPTIONS.map(([value, title, subtitle]) => (
              <label
                key={value}
                className={
                  "flex items-start gap-3 rounded-2xl border p-4 cursor-pointer transition focus-within:ring-2 focus-within:ring-orange-500 " +
                  (confidence === value
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-300 bg-white hover:bg-gray-50")
                }
              >
                <input
                  type="radio"
                  name="birthTimeConfidence"
                  value={value}
                  checked={confidence === value}
                  onChange={() => setFormData({ ...formData, birthTimeConfidence: value })}
                  className="h-5 w-5 mt-0.5 accent-orange-600"
                />
                <span>
                  <span className="block font-semibold text-gray-900">{title}</span>
                  <span className="block text-sm text-gray-600">{subtitle}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {CONFIDENCE_CONSEQUENCE[confidence] ? (
            <p className="text-sm text-gray-600 rounded-2xl bg-gray-50 border border-gray-200 p-4">
              {CONFIDENCE_CONSEQUENCE[confidence]}
            </p>
          ) : null}

          <PrimaryButton onClick={next} disabled={!canContinue}>
            Next
          </PrimaryButton>
        </div>
      </AyupathCard>
    </StepShell>
  );
}
